import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface AnalysisSection {
  id: number;
  section_name: string;
  html_content: string;
  section_order: number;
  updated_at: string;
}

interface TocItem {
  id: string;
  text: string;
}

export default function FullAnalysis() {
  const [htmlContent, setHtmlContent] = useState('');
  const [sections, setSections] = useState<AnalysisSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [dataSource, setDataSource] = useState<'sections' | 'full'>('sections');
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeSection, setActiveSection] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);
  const [showCalculatorDropdown, setShowCalculatorDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileCalculators, setShowMobileCalculators] = useState(false);

  // Process HTML to add IDs to H1 elements and extract TOC
  const processHtmlContent = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const h1Elements = doc.querySelectorAll('h1');
    const items: TocItem[] = [];

    h1Elements.forEach((h1, index) => {
      const id = `section-${index}`;
      h1.setAttribute('id', id);
      items.push({
        id,
        text: h1.textContent || ''
      });
    });

    setTocItems(items);
    return doc.body.innerHTML;
  };

  // Load analysis sections and combine them
  const loadAnalysisSections = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/fetch-analysis-sections`,
        {
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
        }
      );

      const result = await response.json();

      if (result.success && result.sections && result.sections.length > 0) {
        setSections(result.sections);
        
        // Combine all sections in order
        const combinedHtml = result.sections
          .map((section: AnalysisSection) => section.html_content)
          .join('\n\n');
        
        // Process HTML to add IDs and extract TOC
        const processedHtml = processHtmlContent(combinedHtml);
        setHtmlContent(processedHtml);
        setDataSource('sections');
        
        // Get the most recent update time
        const latestUpdate = result.sections.reduce((latest: string, section: AnalysisSection) => {
          return section.updated_at > latest ? section.updated_at : latest;
        }, result.sections[0].updated_at);
        
        setLastUpdate(new Date(latestUpdate));
      } else {
        // Fallback to full analysis if no sections found
        await loadFullAnalysisData();
      }
    } catch (error) {
      console.error('Error loading analysis sections:', error);
      // Fallback to full analysis on error
      await loadFullAnalysisData();
    } finally {
      setIsLoading(false);
    }
  };

  // Fallback: Load full analysis HTML from Supabase
  const loadFullAnalysisData = async () => {
    try {
      const { data, error } = await supabase
        .from('market_analysis_full')
        .select('*')
        .eq('id', 1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data && data.html_content) {
        const processedHtml = processHtmlContent(data.html_content);
        setHtmlContent(processedHtml);
        setLastUpdate(new Date(data.updated_at));
        setDataSource('full');
      }
    } catch (error) {
      console.error('Error loading full analysis data:', error);
    }
  };

  // Handle scroll to section
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      setActiveSection(id);
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (tocItems.length === 0) return;

      const scrollPosition = window.scrollY + 150;

      for (let i = tocItems.length - 1; i >= 0; i--) {
        const item = tocItems[i];
        const element = document.getElementById(item.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(item.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const handleHtmlUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/html') {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const processedHtml = processHtmlContent(event.target.result as string);
          setHtmlContent(processedHtml);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClearContent = () => {
    setHtmlContent('');
    setSections([]);
    setTocItems([]);
  };

  const formatUpdateTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    loadAnalysisSections();
    
    // Refresh data every 2 hours
    const interval = setInterval(() => {
      loadAnalysisSections();
    }, 2 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Navigation */}
      <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-12">
              <Link to="/" className="text-3xl font-bold text-white hover:text-blue-400 transition-colors" style={{ fontFamily: '"Pacifico", serif' }}>
                FinanceHub
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Home</Link>
                <div 
                  className="relative"
                  onMouseEnter={() => setShowCalculatorDropdown(true)}
                  onMouseLeave={() => setShowCalculatorDropdown(false)}
                >
                  <button className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer flex items-center gap-1">
                    Calculators
                    <i className={`ri-arrow-down-s-line text-lg transition-transform ${showCalculatorDropdown ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showCalculatorDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2">
                      <a href="#loan-calculator" className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer">
                        Loan Calculator
                      </a>
                      <a href="#options-calculator" className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer">
                        Options Calculator
                      </a>
                      <a href="#ai-price-prediction" className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer">
                        AI Price Prediction Calculator
                      </a>
                    </div>
                  )}
                </div>
                <Link to="/#news" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">News</Link>
                <Link to="/#real-estate" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Real Estate Data</Link>
                <Link to="/#market-scanner" className="text-gray-300 hover:text-white font-medium transition-colors cursor-pointer">Market Scanner</Link>
                <Link to="/full-analysis" className="text-white font-semibold border-b-2 border-blue-500 pb-1">Full Analysis</Link>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {lastUpdate && (
                <div className="text-xs text-gray-400 hidden lg:block">
                  Updated: {formatUpdateTime(lastUpdate)}
                </div>
              )}
              {dataSource === 'sections' && sections.length > 0 && (
                <div className="text-xs text-gray-400 bg-gray-800 px-3 py-1 rounded-full hidden lg:block">
                  {sections.length} sections loaded
                </div>
              )}
              <button 
                onClick={loadAnalysisSections}
                disabled={isLoading}
                className="hidden md:flex px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 items-center gap-2"
              >
                <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
                Refresh
              </button>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-white hover:bg-gray-800 rounded-lg transition-colors"
              >
                <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {showMobileMenu && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
              <div className="flex flex-col gap-2">
                <Link 
                  to="/" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Home
                </Link>
                
                {/* Mobile Calculators Dropdown */}
                <div>
                  <button
                    onClick={() => setShowMobileCalculators(!showMobileCalculators)}
                    className="w-full px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer flex items-center justify-between"
                  >
                    <span>Calculators</span>
                    <i className={`ri-arrow-down-s-line text-lg transition-transform ${showMobileCalculators ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showMobileCalculators && (
                    <div className="ml-4 mt-2 space-y-2">
                      <a 
                        href="#loan-calculator" 
                        className="block px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Loan Calculator
                      </a>
                      <a 
                        href="#options-calculator" 
                        className="block px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Options Calculator
                      </a>
                      <a 
                        href="#ai-price-prediction" 
                        className="block px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        AI Price Prediction Calculator
                      </a>
                    </div>
                  )}
                </div>
                
                <Link 
                  to="/#news" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  News
                </Link>
                <Link 
                  to="/#real-estate" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Real Estate Data
                </Link>
                <Link 
                  to="/#market-scanner" 
                  className="px-4 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Market Scanner
                </Link>
                <Link 
                  to="/full-analysis" 
                  className="px-4 py-2 text-white bg-gray-800 rounded-lg font-semibold cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Full Analysis
                </Link>
                
                {/* Mobile Refresh Button */}
                <button 
                  onClick={() => {
                    loadAnalysisSections();
                    setShowMobileMenu(false);
                  }}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
                  Refresh Data
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-[1920px] mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Table of Contents - Left Sidebar */}
          {tocItems.length > 0 && (
            <div className="hidden lg:block w-44 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-gray-900 rounded-xl p-3 border border-gray-800">
                  <h3 className="text-xs font-bold text-white mb-2 flex items-center gap-1">
                    <i className="ri-list-ordered text-blue-500 text-sm"></i>
                    Contents
                  </h3>
                  <nav className="space-y-1">
                    {tocItems.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-all cursor-pointer flex items-start gap-1.5 ${
                          activeSection === item.id
                            ? 'bg-blue-600 text-white font-semibold'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                        }`}
                      >
                        <span className="text-xs mt-0.5 flex-shrink-0">{index + 1}.</span>
                        <span className="line-clamp-2">{item.text}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Content */}
          <div className="flex-1 max-w-full">
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <i className="ri-bar-chart-box-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">Market Analysis - Full Access</h1>
                    <p className="text-sm text-gray-400">
                      {dataSource === 'sections' 
                        ? 'Multi-section comprehensive analysis' 
                        : 'Comprehensive market insights and technical analysis'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2">
                    <i className="ri-upload-line"></i>
                    <span className="text-sm font-medium">Upload HTML</span>
                    <input
                      type="file"
                      accept=".html"
                      onChange={handleHtmlUpload}
                      className="hidden"
                    />
                  </label>
                  {htmlContent && (
                    <button 
                      onClick={handleClearContent} 
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <i className="ri-delete-bin-line"></i>
                      <span className="text-sm font-medium">Clear</span>
                    </button>
                  )}
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-16">
                  <i className="ri-loader-4-line text-5xl text-blue-500 animate-spin mb-4"></i>
                  <p className="text-gray-400 text-lg">Loading analysis data...</p>
                </div>
              ) : htmlContent ? (
                <div 
                  ref={contentRef}
                  className="analysis-content overflow-y-auto pr-2" 
                  dangerouslySetInnerHTML={{ __html: htmlContent }} 
                />
              ) : (
                <div className="text-center py-16">
                  <i className="ri-file-chart-line text-6xl text-gray-700 mb-4"></i>
                  <p className="text-gray-400 text-lg mb-2">No analysis data available</p>
                  <p className="text-sm text-gray-500">Upload sections via Python script or upload HTML manually</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-line-chart-line text-xl text-blue-500"></i>
                <h3 className="text-lg font-bold text-white">FinanceHub</h3>
              </div>
              <p className="text-sm text-gray-400">Your trusted source for real-time financial data and market insights.</p>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3">Markets</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Stocks</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Forex</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Crypto</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Commodities</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/full-analysis" className="hover:text-white transition-colors cursor-pointer">Full Analysis</Link></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Calendar</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">News</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">Education</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold text-white mb-3">Follow Us</h4>
              <div className="flex gap-3">
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-line"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-line"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm text-gray-400">
            <p>&copy; 2024 FinanceHub. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>

      <style>{`
        /* Analysis Content Dark Theme Styling */
        .analysis-content {
          color: #e5e7eb;
          line-height: 1.6;
        }
        
        /* Headings */
        .analysis-content h1 {
          color: #ffffff;
          font-size: 1.875rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          margin-top: 2rem;
          border-bottom: 2px solid #374151;
          padding-bottom: 0.5rem;
          scroll-margin-top: 100px;
        }
        
        .analysis-content h2 {
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          margin-top: 1.5rem;
        }
        
        .analysis-content h3 {
          color: #f3f4f6;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          margin-top: 1.25rem;
        }
        
        .analysis-content h4 {
          color: #f3f4f6;
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 1rem;
        }
        
        .analysis-content h5, .analysis-content h6 {
          color: #d1d5db;
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          margin-top: 0.75rem;
        }
        
        /* Paragraphs */
        .analysis-content p {
          color: #9ca3af;
          margin-bottom: 1rem;
          line-height: 1.7;
        }
        
        /* Links */
        .analysis-content a {
          color: #60a5fa;
          text-decoration: underline;
          transition: color 0.2s;
        }
        
        .analysis-content a:hover {
          color: #93c5fd;
        }
        
        /* Lists */
        .analysis-content ul, .analysis-content ol {
          color: #9ca3af;
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        
        .analysis-content li {
          margin-bottom: 0.5rem;
        }
        
        .analysis-content ul li {
          list-style-type: disc;
        }
        
        .analysis-content ol li {
          list-style-type: decimal;
        }
        
        /* Tables */
        .analysis-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1.5rem;
          background: #1f2937;
          border-radius: 0.5rem;
          overflow: hidden;
        }
        
        .analysis-content thead {
          background: #374151;
        }
        
        .analysis-content th {
          color: #ffffff;
          font-weight: 600;
          padding: 0.75rem 1rem;
          text-align: left;
          border-bottom: 2px solid #4b5563;
        }
        
        .analysis-content td {
          color: #d1d5db;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #374151;
        }
        
        .analysis-content tbody tr:hover {
          background: #374151;
        }
        
        .analysis-content tbody tr:last-child td {
          border-bottom: none;
        }
        
        /* Code blocks */
        .analysis-content code {
          background: #1f2937;
          color: #60a5fa;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-family: 'Courier New', monospace;
          font-size: 0.875rem;
        }
        
        .analysis-content pre {
          background: #1f2937;
          color: #e5e7eb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin-bottom: 1rem;
          border: 1px solid #374151;
        }
        
        .analysis-content pre code {
          background: transparent;
          padding: 0;
        }
        
        /* Blockquotes */
        .analysis-content blockquote {
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #d1d5db;
          font-style: italic;
          background: #1f2937;
          padding: 1rem;
          border-radius: 0.25rem;
        }
        
        /* Horizontal rules */
        .analysis-content hr {
          border: none;
          border-top: 1px solid #374151;
          margin: 2rem 0;
        }
        
        /* Images - CENTERED */
        .analysis-content img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 1rem auto;
          border-radius: 0.5rem;
          border: 1px solid #374151;
        }
        
        /* Strong/Bold */
        .analysis-content strong, .analysis-content b {
          color: #ffffff;
          font-weight: 600;
        }
        
        /* Emphasis/Italic */
        .analysis-content em, .analysis-content i {
          color: #d1d5db;
          font-style: italic;
        }
        
        /* Divs and sections */
        .analysis-content div {
          margin-bottom: 0.5rem;
        }
        
        /* Custom classes for charts/figures */
        .analysis-content .chart-container,
        .analysis-content .figure-container {
          background: #1f2937;
          padding: 1rem;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
          border: 1px solid #374151;
        }
        
        .analysis-content .chart-title,
        .analysis-content .figure-title {
          color: #ffffff;
          font-weight: 600;
          margin-bottom: 0.75rem;
          font-size: 1rem;
        }
        
        /* Highlight boxes */
        .analysis-content .highlight,
        .analysis-content .info-box {
          background: #1e3a5f;
          border-left: 4px solid #3b82f6;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
        }
        
        .analysis-content .warning {
          background: #3d2817;
          border-left: 4px solid #f59e0b;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
        }
        
        .analysis-content .success {
          background: #1a3d2e;
          border-left: 4px solid #10b981;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
        }
        
        .analysis-content .error {
          background: #3d1a1a;
          border-left: 4px solid #ef4444;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.25rem;
        }
        
        /* Small text */
        .analysis-content small {
          color: #6b7280;
          font-size: 0.875rem;
        }
        
        /* Subscript and superscript */
        .analysis-content sub, .analysis-content sup {
          color: #9ca3af;
          font-size: 0.75rem;
        }
        
        /* Definition lists */
        .analysis-content dl {
          margin-bottom: 1rem;
        }
        
        .analysis-content dt {
          color: #ffffff;
          font-weight: 600;
          margin-top: 0.5rem;
        }
        
        .analysis-content dd {
          color: #9ca3af;
          margin-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
